import { logger } from './logger';

const ELEMENT_NODE_TYPE = 1;
const DEFAULT_XPATH_QUERY_LIMIT = 200;
const XPATH_FIRST_ORDERED_NODE_TYPE = 9;
const XPATH_ORDERED_NODE_ITERATOR_TYPE = 5;
const HTML_ROOT_TAGS = ['html', 'body'];
const STABLE_XPATH_ATTRIBUTES = ['data-track-id', 'data-testid', 'id'];
const SVG_TAGS = [
  'svg',
  'path',
  'g',
  'image',
  'text',
  'line',
  'rect',
  'polygon',
  'circle',
  'ellipse',
  'use',
  'defs',
  'clipPath',
  'linearGradient',
  'radialGradient',
  'stop',
].map((tag) => tag.toLowerCase());

function isElement(value: unknown): value is Element {
  return Boolean(
    value &&
      typeof value === 'object' &&
      (value as Element).nodeType === ELEMENT_NODE_TYPE
  );
}

/** 元素是否对用户可见（隐藏弹层/display:none/零尺寸视为不可见） */
export function isElementVisible(element: Element): boolean {
  if (!isElement(element) || !element.isConnected) {
    return false;
  }

  const doc = element.ownerDocument;
  const win = doc?.defaultView;
  if (!win) {
    return false;
  }

  try {
    const style = win.getComputedStyle(element);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.opacity === '0'
    ) {
      return false;
    }
  } catch (error) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return rect.width >= 1 && rect.height >= 1;
}

/** 从 xpath 中提取 data-track-id（若有） */
export function extractDataTrackIdFromXpath(xpath?: string): string {
  if (!xpath || typeof xpath !== 'string') {
    return '';
  }
  const quoted = xpath.match(/@data-track-id\s*=\s*(['"])(.*?)\1/i);
  if (quoted && quoted[2]) {
    return quoted[2].trim();
  }
  const bare = xpath.match(/@data-track-id\s*=\s*([^\s\]\/]+)/i);
  return bare && bare[1] ? bare[1].trim() : '';
}

function canQuerySelectorAll(value: unknown): value is ParentNode {
  return Boolean(
    value &&
      typeof value === 'object' &&
      typeof (value as ParentNode).querySelectorAll === 'function'
  );
}

function canEvaluateXpath(value: unknown): value is Document {
  return Boolean(
    value &&
      typeof value === 'object' &&
      typeof (value as Document).evaluate === 'function'
  );
}

function normalizeTagName(element: Element): string {
  return element.tagName.toLowerCase();
}

function isSvgTagName(tagName: string): boolean {
  return SVG_TAGS.indexOf(tagName.toLowerCase()) !== -1;
}

function getXpathTagName(element: Element): string {
  const tagName = normalizeTagName(element);

  if (isSvgTagName(tagName)) {
    return "*[name()='" + tagName + "']";
  }

  return tagName;
}

function getElementIndex(element: Element, tagName: string): number {
  let index = 0;
  let current: Element | null = element;

  while (current) {
    if (normalizeTagName(current) === tagName) {
      index += 1;
    }

    current = current.previousElementSibling;
  }

  return index;
}

function getParentElement(element: Element): Element | null {
  const parent = element.parentElement || element.parentNode;
  return isElement(parent) ? parent : null;
}

function containsSvgElement(element: Element): boolean {
  let current: Element | null = element;

  while (current && isElement(current)) {
    if (isSvgTagName(normalizeTagName(current))) {
      return true;
    }

    current = getParentElement(current);
  }

  return false;
}

function createXpathStringLiteral(value: string): string {
  if (value.indexOf('"') === -1) {
    return '"' + value + '"';
  }

  if (value.indexOf("'") === -1) {
    return "'" + value + "'";
  }

  return 'concat("' + value.replace(/"/g, '", \'"\', "') + '")';
}

/** 过滤框架自动生成的 id，避免刷新后 xpath 失效 */
function isStableAttributeValue(attributeName: string, value: string): boolean {
  if (!value) {
    return false;
  }
  if (attributeName !== 'id') {
    return true;
  }
  if (/^(arco-|rc-|react-|css-|:r)/i.test(value)) {
    return false;
  }
  if (/^\d+$/.test(value)) {
    return false;
  }
  return true;
}

function getStableXpathAttribute(element: Element): {
  name: string;
  value: string;
} | null {
  for (let index = 0; index < STABLE_XPATH_ATTRIBUTES.length; index += 1) {
    const attributeName = STABLE_XPATH_ATTRIBUTES[index];
    const attributeValue = element.getAttribute(attributeName);
    const normalizedValue =
      typeof attributeValue === 'string' ? attributeValue.trim() : '';

    if (
      normalizedValue &&
      isStableAttributeValue(attributeName, normalizedValue)
    ) {
      return {
        name: attributeName,
        value: normalizedValue,
      };
    }
  }

  return null;
}

function createXpathSegment(
  element: Element,
  shouldPreserveStableAttribute: boolean
): {
  segment: string;
  hasStableAttribute: boolean;
} {
  const tagName = normalizeTagName(element);
  const stableAttribute = shouldPreserveStableAttribute
    ? getStableXpathAttribute(element)
    : null;
  const xpathTagName = getXpathTagName(element);

  if (stableAttribute) {
    return {
      segment:
        xpathTagName +
        '[@' +
        stableAttribute.name +
        '=' +
        createXpathStringLiteral(stableAttribute.value) +
        ']',
      hasStableAttribute: true,
    };
  }

  if (HTML_ROOT_TAGS.indexOf(tagName) !== -1) {
    return {
      segment: xpathTagName,
      hasStableAttribute: false,
    };
  }

  return {
    segment: xpathTagName + '[' + getElementIndex(element, tagName) + ']',
    hasStableAttribute: false,
  };
}

export function getStableXpath(element: Element): string | null {
  if (!isElement(element)) {
    return null;
  }

  try {
    const path: string[] = [];
    const hasSvgElement = containsSvgElement(element);
    let hasAddedStableAttribute = false;
    let current: Element | null = element;

    while (current && isElement(current)) {
      const segmentResult = createXpathSegment(
        current,
        hasSvgElement || !hasAddedStableAttribute
      );
      path.push(segmentResult.segment);

      if (segmentResult.hasStableAttribute) {
        hasAddedStableAttribute = true;
      }

      if (segmentResult.hasStableAttribute && !hasSvgElement) {
        return '//' + path.reverse().join('/');
      }

      current = getParentElement(current);
    }

    return '/' + path.reverse().join('/');
  } catch (error) {
    logger.error('生成 XPath 异常', error);
    return null;
  }
}

function resolveXpathDocument(root?: Document | Element): Document | null {
  if (canEvaluateXpath(root)) {
    return root;
  }

  if (isElement(root) && canEvaluateXpath(root.ownerDocument)) {
    return root.ownerDocument;
  }

  if (typeof document !== 'undefined' && canEvaluateXpath(document)) {
    return document;
  }

  return null;
}

function resolveXpathContext(
  root?: Document | Element
): Document | Element | null {
  if (canEvaluateXpath(root) || isElement(root)) {
    return root;
  }

  return resolveXpathDocument(root);
}

export function isXpathSelector(selector: string): boolean {
  if (typeof selector !== 'string') {
    return false;
  }

  const normalizedSelector = selector.trim();
  return (
    normalizedSelector.indexOf('/') === 0 ||
    normalizedSelector.indexOf('(') === 0
  );
}

export function getElementByXpath(
  xpath: string,
  root?: Document | Element
): Element | null {
  if (typeof xpath !== 'string' || xpath.trim().length === 0) {
    return null;
  }

  const doc = resolveXpathDocument(root);
  const contextNode = resolveXpathContext(root);

  if (!doc || !contextNode) {
    return null;
  }

  try {
    const result = doc.evaluate(
      xpath.trim(),
      contextNode,
      null,
      XPATH_FIRST_ORDERED_NODE_TYPE,
      null
    );
    return isElement(result.singleNodeValue) ? result.singleNodeValue : null;
  } catch (error) {
    logger.error('XPath 查询异常：' + xpath, error);
    return null;
  }
}

export function getElementsByXpath(
  xpath: string,
  root?: Document | Element,
  maxCount: number = DEFAULT_XPATH_QUERY_LIMIT
): Element[] {
  if (typeof xpath !== 'string' || xpath.trim().length === 0) {
    return [];
  }

  if (!Number.isFinite(maxCount) || maxCount <= 0) {
    return [];
  }

  const doc = resolveXpathDocument(root);
  const contextNode = resolveXpathContext(root);

  if (!doc || !contextNode) {
    return [];
  }

  try {
    const result = doc.evaluate(
      xpath.trim(),
      contextNode,
      null,
      XPATH_ORDERED_NODE_ITERATOR_TYPE,
      null
    );
    const elements: Element[] = [];
    let current = result.iterateNext();

    while (current && elements.length < maxCount) {
      if (isElement(current)) {
        elements.push(current);
      }

      current = result.iterateNext();
    }

    return elements;
  } catch (error) {
    logger.error('XPath 批量查询异常：' + xpath, error);
    return [];
  }
}

export function resolveTrackingElements(
  selector: string,
  root?: Document | Element
): Element[] {
  if (typeof selector !== 'string' || selector.trim().length === 0) {
    return [];
  }

  const normalizedSelector = selector.trim();

  if (isXpathSelector(normalizedSelector)) {
    return getElementsByXpath(normalizedSelector, root);
  }

  const queryRoot =
    root || (typeof document === 'undefined' ? undefined : document);

  if (!canQuerySelectorAll(queryRoot)) {
    return [];
  }

  try {
    return Array.prototype.slice.call(
      queryRoot.querySelectorAll(normalizedSelector)
    ) as Element[];
  } catch (error) {
    logger.error('CSS selector 查询异常：' + normalizedSelector, error);
    return [];
  }
}
